import i18next from 'i18next';
import { ApiErrorCode, type ParsedApiError } from '@ooh/contracts';

/**
 * Maps known ApiErrorCode values to translated Polish toast copy so the raw
 * English NestJS exception message (`parsed.message`) never reaches a user
 * (query-client-error-toast-i18n). Unmapped codes fall back to a generic
 * "something went wrong" message.
 *
 * `source` distinguishes a QueryCache failure (a background GET the user
 * never directly triggered) from a MutationCache failure (a form/action the
 * user just submitted) — required, not defaulted, so a caller can't silently
 * reuse the wrong wording for its context (query-client-error-toast-i18n).
 *
 * `i18next.t` is called directly (not `useTranslation`) because this helper
 * is used from `query-client.ts`'s `QueryCache`/`MutationCache` `onError`
 * callbacks, which run outside the React tree.
 */
const ERROR_CODE_KEYS: Partial<Record<ApiErrorCode, string>> = {
  [ApiErrorCode.VALIDATION_ERROR]: 'apiErrors.validationError',
  [ApiErrorCode.UNAUTHORIZED]: 'apiErrors.unauthorized',
  [ApiErrorCode.FORBIDDEN]: 'apiErrors.forbidden',
  [ApiErrorCode.NOT_FOUND]: 'apiErrors.notFound',
  [ApiErrorCode.CONFLICT]: 'apiErrors.conflict',
  [ApiErrorCode.RATE_LIMITED]: 'apiErrors.rateLimited',
  [ApiErrorCode.INTERNAL_ERROR]: 'apiErrors.internalError',
  [ApiErrorCode.STALE_STATE]: 'apiErrors.staleState',
  [ApiErrorCode.RESERVATION_CONFLICT]: 'apiErrors.reservationConflict',
  [ApiErrorCode.RESERVATION_STATUS_BLOCKED]: 'apiErrors.reservationStatusBlocked',
  [ApiErrorCode.OFFER_VERSION_SUPERSEDED]: 'apiErrors.offerVersionSuperseded',
  [ApiErrorCode.PUBLIC_LINK_EXPIRED]: 'apiErrors.publicLinkExpired',
  [ApiErrorCode.LOCKED_DECISION_MODIFIED]: 'apiErrors.lockedDecisionModified',
  [ApiErrorCode.UNSUPPORTED_FILE_FORMAT]: 'apiErrors.unsupportedFileFormat',
};

/**
 * VALIDATION_ERROR's shared wording ("the submitted form data is invalid")
 * is wrong for a background query failure — the user never submitted
 * anything. Every other code's wording is already source-agnostic.
 */
const QUERY_ERROR_CODE_KEY_OVERRIDES: Partial<Record<ApiErrorCode, string>> = {
  [ApiErrorCode.VALIDATION_ERROR]: 'apiErrors.queryValidationError',
};

export function apiErrorToUserMessage(
  parsed: ParsedApiError,
  source: 'query' | 'mutation',
): string {
  const key =
    (source === 'query' ? QUERY_ERROR_CODE_KEY_OVERRIDES[parsed.errorCode] : undefined) ??
    ERROR_CODE_KEYS[parsed.errorCode] ??
    'apiErrors.generic';
  // cast: key is resolved dynamically from ApiErrorCode, so it can't be a
  // literal from i18next's generated key union — see t(key as never) precedent
  // at src/routes/map/index.tsx.
  return i18next.t(key as never, { ns: 'common' });
}
